import { Link } from 'react-router-dom';
import {
  OVERVIEW_MODULE_LABELS,
  overviewTargetPath,
  type OverviewNode,
  type OverviewNodeId,
} from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { NODE_ICON, StatePill } from './parts.js';

/**
 * SCREENPLAY → SCENES → LOCATIONS | PRODUCTION ELEMENTS, in one strip.
 *
 * Each stage says four things and no more: its name, its state, one figure and
 * where it opens. The detail behind the figure (the funnel, the category
 * matrix) is the "Progress detail" under the strip — level 3, there when asked
 * for, never a second set of cards repeating the first.
 *
 * THE FORK IS STILL THE POINT. Locations and the production elements both read
 * the confirmed scenes and neither waits for the other, so they share the last
 * column, one above the other, under the words "either order" — never drawn as
 * a fourth step after a third.
 */
export function WorkflowMap({
  nodes,
  address,
}: {
  nodes: Readonly<Record<OverviewNodeId, OverviewNode>>;
  address: string;
}) {
  const done = (id: OverviewNodeId) => nodes[id].state === 'COMPLETED';

  return (
    <section className="cc-panel" data-testid="overview-workflow-map" aria-labelledby="overview-map-title">
      <div className="cc-panel-head">
        <h2 className="cc-panel-title" id="overview-map-title">
          Production workflow
        </h2>
      </div>

      <ol className="cc-flow">
        <li className="cc-flow-stage">
          <Node node={nodes.SCREENPLAY} address={address} />
        </li>
        <li className="cc-flow-arrow" aria-hidden="true" data-on={done('SCREENPLAY') ? '1' : '0'}>
          <Icon name="arrowRight" size={16} />
        </li>
        <li className="cc-flow-stage">
          <Node node={nodes.SCENES} address={address} />
        </li>
        <li className="cc-flow-arrow" aria-hidden="true" data-on={done('SCENES') ? '1' : '0'}>
          <Icon name="arrowRight" size={16} />
        </li>
        <li className="cc-flow-stage cc-flow-fork">
          <p className="cc-flow-either" data-testid="overview-branch-note">
            Either order
          </p>
          <ul className="cc-flow-branches">
            <li>
              <Node node={nodes.LOCATIONS} address={address} />
            </li>
            <li>
              <Node node={nodes.BREAKDOWN} address={address} />
            </li>
          </ul>
        </li>
      </ol>
    </section>
  );
}

function Node({ node, address }: { node: OverviewNode; address: string }) {
  const name = OVERVIEW_MODULE_LABELS[node.id];
  const body = (
    <>
      <div className="cc-node-head">
        <span className="cc-node-name">
          <Icon name={NODE_ICON[node.id]} size={15} />
          {name}
        </span>
        <StatePill state={node.state} testId={`overview-node-${node.id}-state`} />
      </div>
      <p className="cc-node-headline" data-testid={`overview-node-${node.id}-headline`}>
        {node.headline}
      </p>
      {node.detail === null ? null : (
        <p className="cc-node-detail" data-testid={`overview-node-${node.id}-detail`}>
          {node.detail}
        </p>
      )}
      {node.target === null ? null : (
        <div className="cc-node-foot">
          <span className="cc-node-attention" data-testid={`overview-node-${node.id}-attention`}>
            {node.attention > 0 ? `${String(node.attention)} waiting on you` : ''}
          </span>
          <span className="cc-node-open" aria-hidden="true">
            Open <Icon name="arrowRight" size={13} />
          </span>
        </div>
      )}
    </>
  );

  if (node.target === null) {
    return (
      <div className="cc-node" aria-disabled="true" data-state={node.state} data-testid={`overview-node-${node.id}`}>
        {body}
      </div>
    );
  }

  return (
    <Link
      to={overviewTargetPath(address, node.target)}
      className="cc-node"
      data-state={node.state}
      data-testid={`overview-node-${node.id}`}
    >
      {body}
    </Link>
  );
}
